<template>
  <AppHeader />
  <div class="min-h-screen bg-gradient-to-b from-blue-900 via-blue-700 to-blue-200 flex flex-col">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-blue-900 to-blue-700 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col items-center justify-center text-center">
        <div class="absolute inset-0 pointer-events-none">
          <svg class="w-full h-full opacity-10" fill="none" viewBox="0 0 1440 320">
            <path fill="#fff" fill-opacity="1" d="M0,160L80,170.7C160,181,320,203,480,197.3C640,192,800,160,960,133.3C1120,107,1280,85,1360,74.7L1440,64L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"></path>
          </svg>
        </div>
        <h1 class="text-4xl sm:text-5xl font-extrabold text-white drop-shadow-lg mb-4 tracking-tight">
          Success Boost
        </h1>
        <p class="text-xl sm:text-2xl text-blue-100 max-w-2xl mx-auto mb-8 font-medium drop-shadow">
          Academic support made simple for South African university students
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-4 mb-8 z-10">
          <template v-if="isLoggedIn">
            <router-link
              to="/profile"
              class="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-semibold rounded-lg shadow-lg hover:from-yellow-500 hover:to-yellow-400 transition-colors text-lg"
            >
              Go to Dashboard
            </router-link>
            <button
              @click="logout"
              class="px-8 py-3 border border-yellow-400 text-yellow-100 bg-blue-900 font-semibold rounded-lg hover:bg-blue-800 hover:text-yellow-400 transition-colors text-lg"
            >
              Sign Out
            </button>
          </template>
          <template v-else>
            <router-link
              to="/register"
              class="px-8 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 font-semibold rounded-lg shadow-lg hover:from-yellow-500 hover:to-yellow-400 transition-colors text-lg"
            >
              Get Started
            </router-link>
            <router-link
              to="/login"
              class="px-8 py-3 border border-yellow-400 text-yellow-100 bg-blue-900 font-semibold rounded-lg hover:bg-blue-800 hover:text-yellow-400 transition-colors text-lg"
            >
              Sign In
            </router-link>
          </template>
        </div>
        <div class="flex flex-wrap justify-center gap-4 mt-8 z-10">
          <div class="bg-white/80 rounded-xl shadow-lg px-6 py-4 flex flex-col items-center w-60">
            <span class="material-symbols-outlined text-blue-700 text-4xl mb-2">school</span>
            <span class="font-semibold text-blue-900 text-lg mb-1">Personalized Timetables</span>
            <span class="text-blue-800 text-sm">Manage lectures, tests, and more in one place.</span>
          </div>
          <div class="bg-white/80 rounded-xl shadow-lg px-6 py-4 flex flex-col items-center w-60">
            <span class="material-symbols-outlined text-blue-700 text-4xl mb-2">groups</span>
            <span class="font-semibold text-blue-900 text-lg mb-1">Student Community</span>
            <span class="text-blue-800 text-sm">Connect, share, and grow with your peers.</span>
          </div>
          <div class="bg-white/80 rounded-xl shadow-lg px-6 py-4 flex flex-col items-center w-60">
            <span class="material-symbols-outlined text-blue-700 text-4xl mb-2">auto_graph</span>
            <span class="font-semibold text-blue-900 text-lg mb-1">Academic Insights</span>
            <span class="text-blue-800 text-sm">Track your progress and stay motivated.</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex-1">
      <div class="bg-white/90 rounded-2xl shadow-xl p-10">
        <h2 class="text-2xl font-bold text-blue-900 mb-6 text-center">Why Success Boost?</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="flex flex-col items-center">
            <span class="material-symbols-outlined text-yellow-500 text-5xl mb-3">event_available</span>
            <h3 class="font-semibold text-blue-900 text-lg mb-2">All-in-One Academic Hub</h3>
            <p class="text-gray-700 text-center">Centralize your schedules, deadlines, and resources for a stress-free student life.</p>
          </div>
          <div class="flex flex-col items-center">
            <span class="material-symbols-outlined text-yellow-500 text-5xl mb-3">chat</span>
            <h3 class="font-semibold text-blue-900 text-lg mb-2">Peer Support & Networking</h3>
            <p class="text-gray-700 text-center">Join a vibrant community, ask questions, and collaborate with fellow students.</p>
          </div>
          <div class="flex flex-col items-center">
            <span class="material-symbols-outlined text-yellow-500 text-5xl mb-3">workspace_premium</span>
            <h3 class="font-semibold text-blue-900 text-lg mb-2">Premium Experience</h3>
            <p class="text-gray-700 text-center">Enjoy a modern, intuitive interface designed for your success and comfort.</p>
          </div>
        </div>
        <div class="mt-10 text-center">
          <span class="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full font-medium shadow">
            🚀 Currently under development – more features coming soon!
          </span>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-white border-t border-gray-200 py-6 mt-auto">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p class="text-sm text-gray-500">
          © {{ currentYear }} Success Boost &middot; Made with <span class="text-red-500">♥</span> for SA students
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import { logoutUser } from '@/composables/useAuth';

export default {
  name: "AppLanding",
  components: {
    AppHeader
  },
  data() {
    return {
      currentYear: new Date().getFullYear()
    };
  },
  computed: {
    isLoggedIn() {
      // Adjust this according to your auth state management
      return !!this.$store.state.user;
    }
  },
  methods: {
    async logout() {
      await logoutUser();
      this.$router.push('/login');
    }
  }
}
</script>

<style scoped>
/* Add a subtle fade-in for premium feel */
section, footer {
  animation: fadeIn 0.7s cubic-bezier(.4,0,.2,1);
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px);}
  to { opacity: 1; transform: none;}
}
</style>